import { useEffect, useState } from "react";
import { DatePicker } from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { I18nProvider } from "@react-aria/i18n";
import { Button, TextInput, StaticSelect, CustomModal, FormError, showToast } from "../shared";
import { extractBackendErrorMessage, toIsoDate, toUsDate } from "../../utils/utils";
import { useProjectDetails, useUpdateProject } from "../../lib/react-query/tasks";
import { STATUS_OPTIONS } from "../../constants/statuses";

const EditProjectModal = ({ isOpen, onClose, projectId, initialProject }) => {
  const { data: fetchedProject } = useProjectDetails(isOpen ? projectId : null, isOpen);
  const project = initialProject || fetchedProject;
  const { mutateAsync: updateProject, isLoading: updating } = useUpdateProject();
  const [state, setState] = useState({ loading: false, error: null });
  const [form, setForm] = useState({ title: "", description: "", status: "", start_date: "", end_date: "" });
  const startIso = toIsoDate(form.start_date);
  const endIso = toIsoDate(form.end_date);

  useEffect(() => {
    if (project) {
      setForm({
        title: project?.title || "",
        description: project?.description || "",
        status: project?.status || "",
        start_date: project?.start_date || "",
        end_date: project?.end_date || "",
      });
    }
    if (!isOpen) {
      setState({ loading: false, error: null });
    }
  }, [project, isOpen]);

  const parseError = (err) => extractBackendErrorMessage(err, "Failed to update project");

  const handleSave = async () => {
    try {
      setState({ loading: true, error: null });
      const payload = {
        title: form.title,
        description: form.description,
        status: form.status,
        start_date: form.start_date,
        end_date: form.end_date,
      };
      await updateProject({ projectId, payload });
      onClose?.();
      setState({ loading: false, error: null });
    } catch (err) {
      const message = parseError(err);
      setState({ loading: false, error: message });
      showToast(message, "error", { position: "top-center" });
    }
  };

  return (
    <CustomModal
      title={"Edit Project"}
      open={isOpen}
      close={onClose}
      primaryButtonText={"Save"}
      primaryButtonClick={handleSave}
      secondaryButtonText={"Cancel"}
      secondaryButtonClick={onClose}
    >
      <div className="flex flex-col gap-3">
        <TextInput label="Title" type="text" name="title" value={form.title} onChange={(e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }))} />
        <TextInput label="Description" type="text" name="description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }))} />
        <StaticSelect
          options={STATUS_OPTIONS}
          label="Status"
          name="status"
          value={form.status}
          onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
        />
        <I18nProvider locale="en-US">
          <DatePicker
            label="Start Date"
            disableAnimation
            value={startIso ? parseDate(startIso) : null}
            onChange={(date) => setForm((p) => ({ ...p, start_date: date ? toUsDate(date.toString()) : "" }))}
            showMonthAndYearPickers
          />
          <DatePicker
            label="End Date"
            disableAnimation
            value={endIso ? parseDate(endIso) : null}
            onChange={(date) => setForm((p) => ({ ...p, end_date: date ? toUsDate(date.toString()) : "" }))}
            minValue={startIso ? parseDate(startIso) : undefined}
            showMonthAndYearPickers
          />
        </I18nProvider>
        <FormError message={state.error} />
        <Button className="hidden" disabled={state.loading || updating} />
      </div>
    </CustomModal>
  );
};

export default EditProjectModal;
